<script setup>
import { computed, onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import { useAccount } from 'dashboard/composables/useAccount';
import Button from 'dashboard/components-next/button/Button.vue';

const professionals = ref([]);
const services = ref([]);
const establishments = ref([]);
const appointments = ref([]);
const retentionRules = ref([]);
const isLoading = ref(true);
const errorMessage = ref('');
const successMessage = ref('');
const selectedProfessional = ref(null);
const scheduleWindows = ref([]);
const selectedServiceIds = ref([]);
const selectedEstablishmentIds = ref([]);
const professionalForm = ref({});
const newService = ref({ name: '', code: '', description: '', durationMinutes: 60, price: '', currency: 'EUR', priceNote: '', retentionEnabled: false, defaultReturnDays: '', recoveryLeadDays: 2, recoveryCooldownDays: 7 });
const selectedService = ref(null);
const serviceForm = ref({});
const retentionRuleForm = ref({ establishmentId: '', returnDays: '', recoveryLeadDays: 2, recoveryCooldownDays: 7 });
const activeSection = ref('team');
const professionalTab = ref('details');
const newProfessional = ref({
  name: '',
  specialty: '',
  phone: '',
  email: '',
  color: '#2563eb',
});
const router = useRouter();
const { accountId } = useAccount();

const activeProfessionals = computed(() =>
  professionals.value.filter(professional => professional.is_active)
);
const weekdays = [
  { value: 1, label: 'Seg' },
  { value: 2, label: 'Ter' },
  { value: 3, label: 'Qua' },
  { value: 4, label: 'Qui' },
  { value: 5, label: 'Sex' },
  { value: 6, label: 'Sab' },
  { value: 0, label: 'Dom' },
];

function formatAppointmentDate(appointment) {
  const date = new Date(`${appointment.scheduled_date}T00:00:00`);
  const formatted = date.toLocaleDateString('pt-BR', {
    weekday: 'short',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
  return `${formatted}, ${appointment.scheduled_time.slice(0, 5)}`;
}

function statusLabel(status) {
  const labels = {
    confirmed: 'Confirmado',
    cancelled: 'Cancelado',
    rescheduled: 'Reagendado',
    completed: 'Concluido',
    no_show: 'Nao compareceu',
    pending_confirmation: 'Pendente',
  };
  return labels[status] || status;
}

function statusClass(status) {
  const classes = {
    confirmed: 'bg-n-teal-3 text-n-teal-11',
    cancelled: 'bg-n-ruby-3 text-n-ruby-11',
    rescheduled: 'bg-n-amber-3 text-n-amber-11',
    completed: 'bg-n-slate-3 text-n-slate-11',
    no_show: 'bg-n-ruby-3 text-n-ruby-11',
    pending_confirmation: 'bg-n-amber-3 text-n-amber-11',
  };
  return classes[status] || 'bg-n-slate-3 text-n-slate-11';
}

function priceCents(value) {
  if (value === '' || value === null || value === undefined) return null;
  return Math.round(Number(value) * 100);
}

function formatPrice(service) {
  if (service.price_cents === null || service.price_cents === undefined) return 'Preco nao informado';
  return new Intl.NumberFormat('pt-PT', {
    style: 'currency',
    currency: service.currency || 'EUR',
  }).format(service.price_cents / 100);
}

function linkedEstablishmentNames(professional) {
  const names = (professional.professional_establishments || [])
    .filter(link => link.is_active)
    .map(link => link.establishments?.name)
    .filter(Boolean);
  return names.length ? names.join(', ') : 'Sem unidade vinculada';
}

function serviceRetentionLabel(service) {
  if (!service.retention_enabled) return 'Retorno automatico desativado';
  return `Retorno a cada ${service.default_return_days} dias`;
}

function selectedServiceRules() {
  if (!selectedService.value) return [];
  return retentionRules.value.filter(rule => rule.service_id === selectedService.value.id);
}

function appointmentProfessionals(appointment) {
  if (!appointment.establishment_id) return activeProfessionals.value;
  return activeProfessionals.value.filter(professional =>
    (professional.professional_establishments || []).some(
      link => link.is_active && link.establishment_id === appointment.establishment_id
    )
  );
}

async function fetchData() {
  isLoading.value = true;
  errorMessage.value = '';

  try {
    const [professionalsResponse, appointmentsResponse, servicesResponse, establishmentsResponse, retentionRulesResponse] = await Promise.all([
      fetch('/api/superwoot/professionals?includeInactive=true'),
      fetch('/api/superwoot/appointments'),
      fetch('/api/superwoot/services'),
      fetch('/api/superwoot/establishments?includeInactive=true'),
      fetch('/api/superwoot/retention-rules'),
    ]);
    if (!professionalsResponse.ok || !appointmentsResponse.ok || !servicesResponse.ok || !establishmentsResponse.ok || !retentionRulesResponse.ok) {
      throw new Error('request_failed');
    }
    const [professionalsPayload, appointmentsPayload, servicesPayload, establishmentsPayload, retentionRulesPayload] = await Promise.all([
      professionalsResponse.json(),
      appointmentsResponse.json(),
      servicesResponse.json(),
      establishmentsResponse.json(),
      retentionRulesResponse.json(),
    ]);
    professionals.value = professionalsPayload.professionals || [];
    appointments.value = appointmentsPayload.appointments || [];
    services.value = servicesPayload.services || [];
    establishments.value = establishmentsPayload.establishments || [];
    retentionRules.value = retentionRulesPayload.retentionRules || [];
  } catch {
    errorMessage.value = 'Nao foi possivel carregar profissionais e agendamentos.';
  } finally {
    isLoading.value = false;
  }
}

async function createProfessional() {
  if (!newProfessional.value.name.trim()) {
    errorMessage.value = 'Informe o nome do profissional.';
    return;
  }

  try {
    const response = await fetch('/api/superwoot/professionals', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(newProfessional.value),
    });
    if (!response.ok) throw new Error('request_failed');
    newProfessional.value = {
      name: '',
      specialty: '',
      phone: '',
      email: '',
      color: '#2563eb',
    };
    successMessage.value = 'Profissional cadastrado.';
    await fetchData();
  } catch {
    errorMessage.value = 'Nao foi possivel cadastrar o profissional.';
  }
}

async function toggleProfessional(professional) {
  try {
    const response = await fetch('/api/superwoot/professionals', {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        professionalId: professional.id,
        isActive: !professional.is_active,
      }),
    });
    if (!response.ok) throw new Error('request_failed');
    await fetchData();
  } catch {
    errorMessage.value = 'Nao foi possivel atualizar o profissional.';
  }
}

function editSchedule(professional, tab = 'details') {
  selectedProfessional.value = professional;
  professionalTab.value = tab;
  professionalForm.value = {
    name: professional.name,
    specialty: professional.specialty || '',
    phone: professional.phone || '',
    email: professional.email || '',
    color: professional.color || '#2563eb',
  };
  selectedServiceIds.value = (professional.professional_services || [])
    .filter(link => link.is_active)
    .map(link => link.service_id);
  selectedEstablishmentIds.value = (professional.professional_establishments || [])
    .filter(link => link.is_active)
    .map(link => link.establishment_id);
  scheduleWindows.value = (professional.professional_availability_windows || [])
    .filter(window => window.is_active)
    .map(window => ({
      weekday: window.weekday,
      startTime: window.start_time.slice(0, 5),
      endTime: window.end_time.slice(0, 5),
    }));
}

function closeProfessionalEditor() {
  selectedProfessional.value = null;
}

async function saveProfessional() {
  if (!selectedProfessional.value || !professionalForm.value.name?.trim()) return;
  try {
    const response = await fetch('/api/superwoot/professionals', {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        professionalId: selectedProfessional.value.id,
        ...professionalForm.value,
        serviceIds: selectedServiceIds.value,
        establishmentIds: selectedEstablishmentIds.value,
      }),
    });
    if (!response.ok) throw new Error('request_failed');
    successMessage.value = 'Dados do profissional atualizados.';
    await fetchData();
  } catch {
    errorMessage.value = 'Nao foi possivel atualizar os dados do profissional.';
  }
}

async function createService() {
  if (!newService.value.name.trim()) {
    errorMessage.value = 'Informe o nome do servico.';
    return;
  }
  try {
    const response = await fetch('/api/superwoot/services', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...newService.value, priceCents: priceCents(newService.value.price) }),
    });
    const payload = await response.json();
    if (!response.ok) throw new Error(payload.error || 'request_failed');
    newService.value = { name: '', code: '', description: '', durationMinutes: 60, price: '', currency: 'EUR', priceNote: '', retentionEnabled: false, defaultReturnDays: '', recoveryLeadDays: 2, recoveryCooldownDays: 7 };
    successMessage.value = 'Servico cadastrado.';
    await fetchData();
  } catch (error) {
    errorMessage.value =
      error.message === 'service_code_already_exists'
        ? 'Ja existe um servico com este codigo.'
        : 'Nao foi possivel cadastrar o servico.';
  }
}

async function toggleService(service) {
  try {
    const response = await fetch('/api/superwoot/services', {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ serviceId: service.id, isActive: !service.is_active }),
    });
    if (!response.ok) throw new Error('request_failed');
    await fetchData();
  } catch {
    errorMessage.value = 'Nao foi possivel atualizar o servico.';
  }
}

function editService(service) {
  selectedService.value = service;
  serviceForm.value = {
    name: service.name,
    code: service.code,
    durationMinutes: service.duration_minutes,
    description: service.description || '',
    price: service.price_cents === null || service.price_cents === undefined ? '' : service.price_cents / 100,
    currency: service.currency || 'EUR',
    priceNote: service.price_note || '',
    retentionEnabled: service.retention_enabled === true,
    defaultReturnDays: service.default_return_days || '',
    recoveryLeadDays: service.recovery_lead_days ?? 2,
    recoveryCooldownDays: service.recovery_cooldown_days ?? 7,
  };
}

async function saveRetentionRule() {
  if (!selectedService.value || !retentionRuleForm.value.establishmentId || !retentionRuleForm.value.returnDays) {
    errorMessage.value = 'Informe unidade e intervalo da regra.';
    return;
  }
  try {
    const response = await fetch('/api/superwoot/retention-rules', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        serviceId: selectedService.value.id,
        ...retentionRuleForm.value,
      }),
    });
    if (!response.ok) throw new Error('request_failed');
    retentionRuleForm.value = { establishmentId: '', returnDays: '', recoveryLeadDays: 2, recoveryCooldownDays: 7 };
    successMessage.value = 'Regra de retorno da unidade salva.';
    await fetchData();
  } catch {
    errorMessage.value = 'Nao foi possivel salvar a regra de retorno.';
  }
}

async function deleteRetentionRule(rule) {
  try {
    const response = await fetch(`/api/superwoot/retention-rules?retentionRuleId=${encodeURIComponent(rule.id)}`, { method: 'DELETE' });
    if (!response.ok) throw new Error('request_failed');
    await fetchData();
  } catch {
    errorMessage.value = 'Nao foi possivel remover a regra de retorno.';
  }
}

async function saveService() {
  if (!selectedService.value || !serviceForm.value.name?.trim()) return;
  try {
    const response = await fetch('/api/superwoot/services', {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        serviceId: selectedService.value.id,
        ...serviceForm.value,
        priceCents: priceCents(serviceForm.value.price),
      }),
    });
    const payload = await response.json();
    if (!response.ok) throw new Error(payload.error || 'request_failed');
    selectedService.value = null;
    serviceForm.value = {};
    successMessage.value = 'Servico atualizado.';
    await fetchData();
  } catch (error) {
    errorMessage.value =
      error.message === 'service_code_already_exists'
        ? 'Ja existe um servico com este codigo.'
        : 'Nao foi possivel atualizar o servico.';
  }
}

function addWindow() {
  scheduleWindows.value.push({ weekday: 1, startTime: '09:00', endTime: '12:00' });
}

function removeWindow(index) {
  scheduleWindows.value.splice(index, 1);
}

async function saveSchedule() {
  if (!selectedProfessional.value) return;
  try {
    const response = await fetch('/api/superwoot/professionals', {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        professionalId: selectedProfessional.value.id,
        windows: scheduleWindows.value,
      }),
    });
    if (!response.ok) throw new Error('request_failed');
    successMessage.value = 'Disponibilidade atualizada.';
    await fetchData();
  } catch {
    errorMessage.value = 'Nao foi possivel atualizar a disponibilidade.';
  }
}

async function assignProfessional(appointment, professionalId) {
  errorMessage.value = '';
  successMessage.value = '';

  try {
    const response = await fetch('/api/superwoot/appointments', {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        appointmentId: appointment.id,
        status: appointment.status,
        professionalId: professionalId || null,
      }),
    });
    const payload = await response.json();
    if (!response.ok) throw new Error(payload.error || 'request_failed');
    successMessage.value = 'Profissional vinculado ao agendamento.';
    await fetchData();
  } catch (error) {
    errorMessage.value =
      error.message === 'professional_slot_unavailable'
        ? 'Este profissional ja possui atendimento nesse horario.'
        : 'Nao foi possivel vincular o profissional.';
  }
}

function openConversation(appointment) {
  const conversationId = appointment.conversations?.external_conversation_id;
  if (!conversationId) return;
  router.push(`/app/accounts/${accountId.value}/conversations/${conversationId}`);
}

onMounted(fetchData);
</script>

<template>
  <section class="flex flex-col flex-1 min-w-0 h-full overflow-auto bg-n-background">
    <header
      class="flex flex-wrap items-center justify-between gap-3 px-6 py-4 border-b border-n-weak bg-n-solid-2"
    >
      <div>
        <h1 class="text-xl font-medium text-n-slate-12">Profissionais</h1>
        <p class="mt-1 text-sm text-n-slate-11">
          Equipe e responsaveis pelos agendamentos
        </p>
      </div>
      <Button
        icon="i-lucide-refresh-cw"
        color="slate"
        size="sm"
        title="Atualizar"
        @click="fetchData"
      />
    </header>

    <p v-if="errorMessage" class="px-6 pt-4 text-sm text-n-ruby-11">
      {{ errorMessage }}
    </p>
    <p v-if="successMessage" class="px-6 pt-4 text-sm text-n-teal-11">
      {{ successMessage }}
    </p>

    <nav class="flex gap-1 px-4 pt-4 overflow-x-auto sm:px-6" aria-label="Areas de profissionais">
      <button
        v-for="tab in [
          { id: 'team', label: 'Equipe', icon: 'i-lucide-users' },
          { id: 'services', label: 'Servicos', icon: 'i-lucide-scissors' },
          { id: 'appointments', label: 'Agendamentos', icon: 'i-lucide-calendar-days' },
        ]"
        :key="tab.id"
        type="button"
        class="flex items-center gap-2 px-3 py-2 text-sm transition-colors border-b-2 whitespace-nowrap"
        :class="activeSection === tab.id ? 'border-n-brand text-n-slate-12' : 'border-transparent text-n-slate-10 hover:text-n-slate-12'"
        @click="activeSection = tab.id"
      >
        <span :class="tab.icon" />
        {{ tab.label }}
      </button>
    </nav>

    <div class="p-4 sm:p-6">
      <section v-if="activeSection === 'team'" class="max-w-3xl min-w-0">
        <h2 class="text-base font-medium text-n-slate-12">Cadastrar profissional</h2>
        <div class="grid gap-3 p-4 mt-3 border rounded-lg border-n-weak bg-n-solid-2">
          <label class="grid gap-1 text-xs text-n-slate-11">
            Nome
            <input
              v-model="newProfessional.name"
              class="h-9 px-3 text-sm border rounded-md border-n-weak bg-n-solid-1 text-n-slate-12"
              placeholder="Nome completo"
            />
          </label>
          <label class="grid gap-1 text-xs text-n-slate-11">
            Especialidade
            <input
              v-model="newProfessional.specialty"
              class="h-9 px-3 text-sm border rounded-md border-n-weak bg-n-solid-1 text-n-slate-12"
              placeholder="Barbeiro, aplicacao..."
            />
          </label>
          <label class="grid gap-1 text-xs text-n-slate-11">
            Telefone
            <input
              v-model="newProfessional.phone"
              class="h-9 px-3 text-sm border rounded-md border-n-weak bg-n-solid-1 text-n-slate-12"
              placeholder="+351..."
            />
          </label>
          <label class="grid gap-1 text-xs text-n-slate-11">
            E-mail
            <input
              v-model="newProfessional.email"
              class="h-9 px-3 text-sm border rounded-md border-n-weak bg-n-solid-1 text-n-slate-12"
              placeholder="profissional@zero14.pt"
            />
          </label>
          <label class="flex items-center gap-2 text-xs text-n-slate-11">
            <input v-model="newProfessional.color" type="color" class="w-9 h-8 border rounded border-n-weak" />
            Cor da agenda
          </label>
          <Button label="Adicionar profissional" color="blue" size="sm" @click="createProfessional" />
        </div>

        <h2 class="mt-6 text-base font-medium text-n-slate-12">
          Equipe cadastrada
        </h2>
        <div class="mt-3 overflow-hidden border rounded-lg border-n-weak bg-n-solid-2">
          <p v-if="!isLoading && professionals.length === 0" class="p-4 text-sm text-n-slate-10">
            Nenhum profissional cadastrado.
          </p>
          <article
            v-for="professional in professionals"
            :key="professional.id"
            class="flex flex-wrap items-center justify-between gap-3 p-3 border-b border-n-weak last:border-b-0"
          >
            <div class="min-w-0">
              <div class="flex items-center gap-2">
                <span class="w-2.5 h-2.5 rounded-full" :style="{ backgroundColor: professional.color }" />
                <strong class="text-sm font-medium truncate text-n-slate-12">
                  {{ professional.name }}
                </strong>
              </div>
              <span class="block mt-1 text-xs truncate text-n-slate-10">
                {{ professional.specialty || 'Sem especialidade informada' }}
              </span>
              <span class="block mt-1 text-xs truncate text-n-slate-10">
                {{ linkedEstablishmentNames(professional) }}
              </span>
            </div>
            <div class="flex flex-wrap justify-end gap-2 ml-auto">
              <Button icon="i-lucide-pencil" color="slate" size="sm" title="Editar profissional" @click="editSchedule(professional, 'details')" />
              <Button icon="i-lucide-calendar-clock" color="slate" size="sm" title="Configurar disponibilidade" @click="editSchedule(professional, 'availability')" />
              <Button
                :label="professional.is_active ? 'Ativo' : 'Inativo'"
                :color="professional.is_active ? 'slate' : 'ruby'"
                size="sm"
                @click="toggleProfessional(professional)"
              />
            </div>
          </article>
        </div>

        <div v-if="selectedProfessional" class="p-4 mt-4 border rounded-lg border-n-weak bg-n-solid-2">
          <div class="flex items-center justify-between gap-2">
            <h2 class="text-sm font-medium text-n-slate-12">Configurar: {{ selectedProfessional.name }}</h2>
            <Button icon="i-lucide-x" color="slate" size="sm" title="Fechar" @click="closeProfessionalEditor" />
          </div>
          <nav class="flex gap-1 mt-3 overflow-x-auto border-b border-n-weak" aria-label="Configuracao do profissional">
            <button
              v-for="tab in [
                { id: 'details', label: 'Dados' },
                { id: 'services', label: 'Servicos' },
                { id: 'establishments', label: 'Unidades' },
                { id: 'availability', label: 'Disponibilidade' },
              ]"
              :key="tab.id"
              type="button"
              class="px-3 py-2 text-xs transition-colors border-b-2 whitespace-nowrap"
              :class="professionalTab === tab.id ? 'border-n-brand text-n-slate-12' : 'border-transparent text-n-slate-10 hover:text-n-slate-12'"
              @click="professionalTab = tab.id"
            >
              {{ tab.label }}
            </button>
          </nav>
          <div v-if="professionalTab === 'details'" class="grid gap-2 mt-3 sm:grid-cols-2">
            <label class="grid gap-1 text-xs text-n-slate-11">
              Nome
              <input v-model="professionalForm.name" class="h-8 px-2 text-xs border rounded-md border-n-weak bg-n-solid-1 text-n-slate-12" />
            </label>
            <label class="grid gap-1 text-xs text-n-slate-11">
              Especialidade
              <input v-model="professionalForm.specialty" class="h-8 px-2 text-xs border rounded-md border-n-weak bg-n-solid-1 text-n-slate-12" />
            </label>
            <label class="grid gap-1 text-xs text-n-slate-11">
              Telefone
              <input v-model="professionalForm.phone" class="h-8 px-2 text-xs border rounded-md border-n-weak bg-n-solid-1 text-n-slate-12" />
            </label>
            <label class="grid gap-1 text-xs text-n-slate-11">
              E-mail
              <input v-model="professionalForm.email" class="h-8 px-2 text-xs border rounded-md border-n-weak bg-n-solid-1 text-n-slate-12" />
            </label>
            <label class="flex items-center gap-2 text-xs text-n-slate-11">
              <input v-model="professionalForm.color" type="color" class="w-9 h-8 border rounded border-n-weak" />
              Cor da agenda
            </label>
            <Button class="sm:col-span-2" label="Salvar dados" color="blue" size="sm" @click="saveProfessional" />
          </div>
          <div v-else-if="professionalTab === 'services'" class="grid gap-2 mt-3">
            <label v-for="service in services.filter(item => item.is_active)" :key="service.id" class="flex items-center gap-2 text-xs text-n-slate-11">
              <input v-model="selectedServiceIds" type="checkbox" :value="service.id" />
              {{ service.name }} ({{ service.duration_minutes }} min)
            </label>
            <Button class="mt-2" label="Salvar servicos" color="blue" size="sm" @click="saveProfessional" />
          </div>
          <div v-else-if="professionalTab === 'establishments'" class="grid gap-2 mt-3">
            <p class="text-xs text-n-slate-10">
              Selecione as unidades onde este profissional atende.
            </p>
            <label
              v-for="establishment in establishments.filter(item => item.is_active)"
              :key="establishment.id"
              class="flex items-start gap-2 p-2 border rounded-md border-n-weak bg-n-solid-1"
            >
              <input v-model="selectedEstablishmentIds" type="checkbox" :value="establishment.id" class="mt-0.5" />
              <span class="min-w-0">
                <strong class="block text-xs font-medium text-n-slate-12">{{ establishment.name }}</strong>
                <span class="block mt-0.5 text-xs truncate text-n-slate-10">{{ establishment.address_line }}</span>
              </span>
            </label>
            <Button class="mt-2" label="Salvar unidades" color="blue" size="sm" @click="saveProfessional" />
          </div>
          <div v-else class="mt-3">
            <div class="flex items-center justify-between gap-2">
              <p class="text-xs text-n-slate-10">Defina os intervalos de atendimento semanal.</p>
              <Button icon="i-lucide-plus" color="slate" size="sm" title="Adicionar intervalo" @click="addWindow" />
            </div>
            <div class="grid gap-2 mt-3">
            <div v-for="(window, index) in scheduleWindows" :key="index" class="grid grid-cols-[64px_minmax(0,1fr)_minmax(0,1fr)_32px] gap-2">
              <select v-model="window.weekday" class="h-8 px-2 text-xs border rounded-md border-n-weak bg-n-solid-1 text-n-slate-12">
                <option v-for="day in weekdays" :key="day.value" :value="day.value">{{ day.label }}</option>
              </select>
              <input v-model="window.startTime" type="time" class="h-8 px-2 text-xs border rounded-md border-n-weak bg-n-solid-1 text-n-slate-12" />
              <input v-model="window.endTime" type="time" class="h-8 px-2 text-xs border rounded-md border-n-weak bg-n-solid-1 text-n-slate-12" />
              <Button icon="i-lucide-trash-2" color="ruby" size="sm" title="Remover intervalo" @click="removeWindow(index)" />
            </div>
            </div>
            <Button class="mt-3" label="Salvar disponibilidade" color="blue" size="sm" @click="saveSchedule" />
          </div>
        </div>
      </section>

      <section v-if="activeSection === 'services'" class="max-w-4xl min-w-0">
        <div>
          <h2 class="text-base font-medium text-n-slate-12">Servicos</h2>
          <p class="mt-1 text-sm text-n-slate-11">Catalogo usado na agenda e no chatbot</p>
          <div class="grid gap-2 p-3 mt-3 border rounded-lg border-n-weak bg-n-solid-2 sm:grid-cols-[1fr_150px_100px_110px_90px_auto]">
            <input v-model="newService.name" class="h-8 px-2 text-xs border rounded-md border-n-weak bg-n-solid-1 text-n-slate-12" placeholder="Nome do servico" />
            <input v-model="newService.code" class="h-8 px-2 text-xs border rounded-md border-n-weak bg-n-solid-1 text-n-slate-12" placeholder="codigo_opcional" />
            <input v-model.number="newService.durationMinutes" type="number" min="15" max="480" step="15" class="h-8 px-2 text-xs border rounded-md border-n-weak bg-n-solid-1 text-n-slate-12" title="Duracao em minutos" />
            <input v-model="newService.price" type="number" min="0" step="0.01" class="h-8 px-2 text-xs border rounded-md border-n-weak bg-n-solid-1 text-n-slate-12" placeholder="Preco" />
            <select v-model="newService.currency" class="h-8 px-2 text-xs border rounded-md border-n-weak bg-n-solid-1 text-n-slate-12">
              <option value="EUR">EUR</option>
              <option value="BRL">BRL</option>
            </select>
            <Button icon="i-lucide-plus" color="blue" size="sm" title="Adicionar servico" @click="createService" />
            <textarea v-model="newService.description" rows="2" class="px-2 py-1 text-xs border rounded-md border-n-weak bg-n-solid-1 text-n-slate-12 sm:col-span-3" placeholder="Descricao objetiva para o atendimento" />
            <input v-model="newService.priceNote" class="h-8 px-2 text-xs border rounded-md border-n-weak bg-n-solid-1 text-n-slate-12 sm:col-span-3" placeholder="Observacao comercial: a partir de, sujeito a avaliacao..." />
          </div>
          <div class="mt-2 overflow-hidden border rounded-lg border-n-weak bg-n-solid-2">
            <article v-for="service in services" :key="service.id" class="flex items-center justify-between gap-3 px-3 py-2 border-b border-n-weak last:border-b-0">
              <div>
                <strong class="block text-sm font-medium text-n-slate-12">{{ service.name }}</strong>
                <span class="text-xs text-n-slate-10">{{ service.code }} · {{ service.duration_minutes }} min · {{ formatPrice(service) }}</span>
                <span v-if="service.price_note" class="block mt-1 text-xs text-n-slate-10">{{ service.price_note }}</span>
                <span class="block mt-1 text-xs" :class="service.retention_enabled ? 'text-n-teal-11' : 'text-n-slate-10'">{{ serviceRetentionLabel(service) }}</span>
              </div>
              <div class="flex gap-2">
                <Button icon="i-lucide-pencil" color="slate" size="sm" title="Editar servico" @click="editService(service)" />
                <Button :label="service.is_active ? 'Ativo' : 'Inativo'" :color="service.is_active ? 'slate' : 'ruby'" size="sm" @click="toggleService(service)" />
              </div>
            </article>
          </div>
          <div v-if="selectedService" class="grid gap-2 p-3 mt-2 border rounded-lg border-n-weak bg-n-solid-2 sm:grid-cols-[1fr_150px_100px_110px_90px_auto]">
            <input v-model="serviceForm.name" class="h-8 px-2 text-xs border rounded-md border-n-weak bg-n-solid-1 text-n-slate-12" />
            <input v-model="serviceForm.code" class="h-8 px-2 text-xs border rounded-md border-n-weak bg-n-solid-1 text-n-slate-12" />
            <input v-model.number="serviceForm.durationMinutes" type="number" min="15" max="480" step="15" class="h-8 px-2 text-xs border rounded-md border-n-weak bg-n-solid-1 text-n-slate-12" />
            <input v-model="serviceForm.price" type="number" min="0" step="0.01" class="h-8 px-2 text-xs border rounded-md border-n-weak bg-n-solid-1 text-n-slate-12" placeholder="Preco" />
            <select v-model="serviceForm.currency" class="h-8 px-2 text-xs border rounded-md border-n-weak bg-n-solid-1 text-n-slate-12">
              <option value="EUR">EUR</option>
              <option value="BRL">BRL</option>
            </select>
            <Button icon="i-lucide-save" color="blue" size="sm" title="Salvar servico" @click="saveService" />
            <textarea v-model="serviceForm.description" rows="2" class="px-2 py-1 text-xs border rounded-md border-n-weak bg-n-solid-1 text-n-slate-12 sm:col-span-3" placeholder="Descricao objetiva para o atendimento" />
            <input v-model="serviceForm.priceNote" class="h-8 px-2 text-xs border rounded-md border-n-weak bg-n-solid-1 text-n-slate-12 sm:col-span-3" placeholder="Observacao comercial" />
            <section class="grid gap-3 p-3 border rounded-md border-n-weak bg-n-solid-1 sm:col-span-6">
              <label class="flex items-center gap-2 text-xs text-n-slate-12">
                <input v-model="serviceForm.retentionEnabled" type="checkbox" />
                Acompanhar retorno deste servico
              </label>
              <div class="grid gap-2 sm:grid-cols-3">
                <label class="grid gap-1 text-xs text-n-slate-11">
                  Retorno padrao em dias
                  <input v-model.number="serviceForm.defaultReturnDays" type="number" min="1" max="730" class="h-8 px-2 border rounded-md border-n-weak bg-n-solid-2 text-n-slate-12" />
                </label>
                <label class="grid gap-1 text-xs text-n-slate-11">
                  Iniciar contato antes
                  <input v-model.number="serviceForm.recoveryLeadDays" type="number" min="0" max="90" class="h-8 px-2 border rounded-md border-n-weak bg-n-solid-2 text-n-slate-12" />
                </label>
                <label class="grid gap-1 text-xs text-n-slate-11">
                  Cooldown em dias
                  <input v-model.number="serviceForm.recoveryCooldownDays" type="number" min="0" max="180" class="h-8 px-2 border rounded-md border-n-weak bg-n-solid-2 text-n-slate-12" />
                </label>
              </div>
              <div class="pt-3 border-t border-n-weak">
                <h4 class="text-xs font-medium text-n-slate-12">Excecoes por unidade</h4>
                <div v-for="rule in selectedServiceRules()" :key="rule.id" class="flex items-center justify-between gap-2 py-2 text-xs border-b border-n-weak text-n-slate-11">
                  <span>{{ rule.establishments?.name }} · {{ rule.return_days }} dias · contato {{ rule.recovery_lead_days }} dias antes</span>
                  <Button icon="i-lucide-trash-2" color="ruby" size="sm" title="Remover regra da unidade" @click="deleteRetentionRule(rule)" />
                </div>
                <div class="grid gap-2 mt-3 sm:grid-cols-[1fr_100px_100px_100px_auto]">
                  <select v-model="retentionRuleForm.establishmentId" class="h-8 px-2 text-xs border rounded-md border-n-weak bg-n-solid-2 text-n-slate-12">
                    <option value="">Selecionar unidade</option>
                    <option v-for="establishment in establishments.filter(item => item.is_active)" :key="establishment.id" :value="establishment.id">{{ establishment.name }}</option>
                  </select>
                  <input v-model.number="retentionRuleForm.returnDays" type="number" min="1" max="730" placeholder="Dias" class="h-8 px-2 text-xs border rounded-md border-n-weak bg-n-solid-2 text-n-slate-12" />
                  <input v-model.number="retentionRuleForm.recoveryLeadDays" type="number" min="0" max="90" title="Antecedencia" class="h-8 px-2 text-xs border rounded-md border-n-weak bg-n-solid-2 text-n-slate-12" />
                  <input v-model.number="retentionRuleForm.recoveryCooldownDays" type="number" min="0" max="180" title="Cooldown" class="h-8 px-2 text-xs border rounded-md border-n-weak bg-n-solid-2 text-n-slate-12" />
                  <Button icon="i-lucide-plus" color="blue" size="sm" title="Salvar regra da unidade" @click="saveRetentionRule" />
                </div>
              </div>
            </section>
          </div>
        </div>
      </section>

      <section v-if="activeSection === 'appointments'" class="min-w-0">
        <div class="flex flex-wrap items-end justify-between gap-2">
          <div>
            <h2 class="text-base font-medium text-n-slate-12">Vincular agendamentos</h2>
            <p class="mt-1 text-sm text-n-slate-11">
              Defina quem atendera cada cliente
            </p>
          </div>
          <span class="text-xs text-n-slate-10">
            {{ appointments.length }} agendamentos
          </span>
        </div>

        <div class="mt-3 overflow-auto border rounded-lg border-n-weak bg-n-solid-2">
          <table class="w-full min-w-[760px] text-left">
            <thead class="border-b border-n-weak bg-n-solid-3">
              <tr class="text-xs text-n-slate-10">
                <th class="px-3 py-3 font-medium">Cliente</th>
                <th class="px-3 py-3 font-medium">Data e horario</th>
                <th class="px-3 py-3 font-medium">Status</th>
                <th class="px-3 py-3 font-medium">Unidade</th>
                <th class="px-3 py-3 font-medium">Profissional</th>
                <th class="px-3 py-3 font-medium">Conversa</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="appointment in appointments"
                :key="appointment.id"
                class="border-b border-n-weak last:border-b-0"
              >
                <td class="px-3 py-3">
                  <strong class="block text-sm font-medium text-n-slate-12">
                    {{ appointment.contact_name }}
                  </strong>
                  <span class="block mt-1 text-xs text-n-slate-10">
                    {{ appointment.contact_phone || 'Telefone nao informado' }}
                  </span>
                </td>
                <td class="px-3 py-3 text-sm text-n-slate-11">
                  {{ formatAppointmentDate(appointment) }}
                </td>
                <td class="px-3 py-3">
                  <span class="px-1.5 py-0.5 text-xs rounded" :class="statusClass(appointment.status)">
                    {{ statusLabel(appointment.status) }}
                  </span>
                </td>
                <td class="px-3 py-3 text-sm text-n-slate-11">
                  {{ appointment.establishments?.name || 'Sem unidade' }}
                </td>
                <td class="px-3 py-3">
                  <select
                    class="w-full h-8 px-2 text-sm border rounded-md border-n-weak bg-n-solid-1 text-n-slate-12"
                    :value="appointment.professional_id || ''"
                    @change="assignProfessional(appointment, $event.target.value)"
                  >
                    <option value="">Sem profissional</option>
                    <option
                      v-for="professional in appointmentProfessionals(appointment)"
                      :key="professional.id"
                      :value="professional.id"
                    >
                      {{ professional.name }}
                    </option>
                  </select>
                </td>
                <td class="px-3 py-3">
                  <Button
                    icon="i-lucide-message-square"
                    color="slate"
                    size="sm"
                    title="Abrir conversa"
                    :disabled="!appointment.conversations?.external_conversation_id"
                    @click="openConversation(appointment)"
                  />
                </td>
              </tr>
              <tr v-if="!isLoading && appointments.length === 0">
                <td colspan="6" class="px-3 py-5 text-sm text-n-slate-10">
                  Nenhum agendamento encontrado.
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>
    </div>
  </section>
</template>
